import React from 'react'
import { useParams } from 'react-router'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { getFormattedSsn } from '@sputnik/components/utils'
import { epochToDate } from '@sputnik/components/utils'
import AddPersonDrawer, {
  AddPersonDrawerOpenable,
} from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/AddPersonDrawer'
import { useGetPeopleInfoDataLazyQuery } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/GetPeopleInfoData.gql'
import { useUpdatePeopleInfoData } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/UpdatePeopleInfoData.gql'
import { AddPersonForm } from '@sputnik/schema/creditApplication/addPerson'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

export interface UpdateAuthorizedSignerDrawerProps extends AddPersonDrawerOpenable {
  personId: string
}

const UpdateAuthorizedSignerDrawer = ({ isShowing, onClose, personId }: UpdateAuthorizedSignerDrawerProps) => {
  const { appId } = useParams()
  const showToast = useToast()
  const { track } = useWebTracking()

  const [getAuthorizedSigner] = useGetPeopleInfoDataLazyQuery({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const [updateAuthorizedSigner] = useUpdatePeopleInfoData({
    onCompleted: () => {
      logInfo('updateAuthorizedSigner', {
        attributes: {
          creditAppId: appId,
          result: `authorized signer updated successfully`,
        },
        eventName: 'updateAuthorizedSigner',
      })
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Authorized signer updated'
          id='sputnik.UpdateAuthorizedSignerDrawer__TE5ZYD'
        />,
        {
          autoHideDelay: 5000,
        },
      )
      onClose()
    },
    onError: (error) => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Unable to update authorized signer'
          id='sputnik.UpdateAuthorizedSignerDrawer__ttplrB'
        />,
        {
          className: 'fs-unmask',
          autoHideDelay: 5000,
        },
      )
      logError(error, {
        attributes: {
          action: 'updateAuthorizedSigner',
          creditAppId: appId,
          result: error,
          message: 'Error while updating authorized signer.',
        },
        eventName: 'updateAuthorizedSigner',
      })
    },
  })

  const initialValues = async (): Promise<AddPersonForm> => {
    const { data } = await getAuthorizedSigner()

    const {
      address,
      dob,
      email,
      firstName,
      lastName,
      ssnLastFour,
      passportCountry,
      passportExpiration,
      passportNumberLastFour,
      passportNumberLength,
      phoneNumber,
      title,
    } = data?.creditApplication?.authorizedSigner ?? {}

    const { city, countryCode, postalCode, state, street, streetAdditional } = address ?? {}

    return {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
      phoneNumber: phoneNumber ?? '',
      title: title ?? '',
      dob: dob ?? '',
      ssn: {
        value: '',
        display: ssnLastFour ? getFormattedSsn(`*****${ssnLastFour}`, true) : '',
      },
      passportCountry: passportCountry ?? undefined,
      passportExpiration: passportExpiration ?? undefined,
      passportNumber: {
        value: '',
        display: passportNumberLastFour
          ? `${new Array((passportNumberLength ?? 9) - 4).fill('*').join('')}${passportNumberLastFour}`
          : undefined,
      },
      isNonUSCitizen: !!passportCountry || !!passportExpiration || !!passportNumberLastFour,
      isForeignAddress: countryCode && countryCode !== 'US' ? 'YES' : 'NO',
      street: street ?? '',
      streetAdditional: streetAdditional ?? '',
      city: city ?? '',
      state: state ?? '',
      postalCode: postalCode ?? '',
      countryCode: countryCode ? countryCode : 'US',
    }
  }

  const onSubmit = async (formValues: AddPersonForm) => {
    track('se_signup__edit_person_drawer_saved__fired', {
      person_type: 'authorizedSigner',
    })
    await updateAuthorizedSigner({
      variables: {
        creditApplicationId: appId ?? '',
        authorizedSigner: {
          id: personId,
          dob: epochToDate(formValues.dob) || '',
          email: formValues.email || '',
          firstName: formValues.firstName || '',
          lastName: formValues.lastName || '',
          title: formValues.title || '',
          phoneNumber: formValues.phoneNumber || '',
          ...(formValues.ssn?.value && {
            ssn: formValues.ssn?.value,
            passportNumber: null,
            passportCountry: null,
            passportExpiration: null,
          }),
          ...(formValues.passportNumber?.value &&
            formValues.passportExpiration &&
            formValues.passportCountry && {
              passportNumber: formValues.passportNumber?.value,
              passportCountry: formValues.passportCountry,
              passportExpiration: formValues.passportExpiration,
              ssn: null,
            }),
          address: {
            street: formValues.street || '',
            streetAdditional: formValues.streetAdditional || '',
            city: formValues.city || '',
            state: formValues.state || '',
            postalCode: formValues.postalCode || '',
            countryCode: formValues.countryCode || 'US',
          },
        },
      },
    })
  }

  return (
    isShowing && (
      <AddPersonDrawer
        isAuthorizedSigner
        initialValues={initialValues}
        isShowing={isShowing}
        title={
          <FormattedMessage
            defaultMessage='Update authorized signer'
            id='sputnik.UpdateAuthorizedSignerDrawer__QAhHp7'
          />
        }
        onClose={() => {
          track('se_signup__edit_person_drawer_cancelled__fired', {
            person_type: 'authorizedSigner',
          })
          onClose()
        }}
        onSubmit={onSubmit}
      />
    )
  )
}

export default UpdateAuthorizedSignerDrawer
